
import { Component, Vue } from 'vue-property-decorator';

type TlpfRuntime = {
  translateAll: (lang: string) => void;
};

const TLPF_INSTANCE_KEYS = ['__tlpfInstance', 'TLPF'] as const;
type TlpfInstanceKey = typeof TLPF_INSTANCE_KEYS[number];

declare global {
  interface Window {
    __tlpfInstance?: TlpfRuntime;
    TLPF?: TlpfRuntime;
  }
}

@Component
export default class Tlpf extends Vue {
  activeLang = 'ja';

  onStorage: ((event: StorageEvent) => void) | null = null;

  tlpfWatchInstalled = false;

  tlpfWatchCurrent: Partial<Record<TlpfInstanceKey, TlpfRuntime | undefined>> | null = null;

  getTlpfInstance(): TlpfRuntime | null {
    if (window.__tlpfInstance && typeof window.__tlpfInstance.translateAll === 'function') {
      return window.__tlpfInstance;
    }
    if (window.TLPF && typeof window.TLPF.translateAll === 'function') {
      return window.TLPF;
    }
    return null;
  }

  getSavedLang(): string {
    const cookieLang = this.$cookies && this.$cookies.get('tlpf_locale');
    const saved = (typeof cookieLang === 'string' && cookieLang) || localStorage.getItem('tlpf_locale');
    return saved && saved.toLowerCase().startsWith('en') ? 'en' : 'ja';
  }

  switchLang(lang: string): void {
    this.activeLang = lang;
    localStorage.setItem('tlpf_locale', lang);
    const tlpf = this.getTlpfInstance();
    if (tlpf) {
      tlpf.translateAll(lang);
    }
  }

  saveLang(): void {
    this.activeLang = this.getSavedLang();
  }

  applySavedLang(): boolean {
    this.saveLang();
    const tlpf = this.getTlpfInstance();
    if (!tlpf) {
      return false;
    }
    tlpf.translateAll(this.activeLang);
    return true;
  }

  watchTlpfAssignment(): void {
    if (this.applySavedLang()) {
      return;
    }

    this.tlpfWatchInstalled = true;
    this.tlpfWatchCurrent = {};

    TLPF_INSTANCE_KEYS.forEach((key) => {
      try {
        this.tlpfWatchCurrent![key] = window[key];
        Object.defineProperty(window, key, {
          configurable: true,
          enumerable: true,
          get: () => this.tlpfWatchCurrent![key],
          set: (value: TlpfRuntime | undefined) => {
            this.tlpfWatchCurrent![key] = value;
            if (this.applySavedLang()) {
              this.restoreTlpfWatch();
            }
          },
        });
      } catch (error) {
        console.error('[Tlpf] failed to watch', key, error);
      }
    });
  }

  restoreTlpfWatch(): void {
    if (!this.tlpfWatchInstalled) {
      return;
    }
    TLPF_INSTANCE_KEYS.forEach((key) => {
      const value = this.tlpfWatchCurrent ? this.tlpfWatchCurrent[key] : window[key];
      try {
        Object.defineProperty(window, key, {
          configurable: true,
          enumerable: true,
          writable: true,
          value,
        });
      } catch (error) {
        window[key] = value;
      }
    });
    this.tlpfWatchInstalled = false;
    this.tlpfWatchCurrent = null;
  }

  mounted(): void {
    this.watchTlpfAssignment();

    this.onStorage = (event: StorageEvent) => {
      if (!event || event.key === 'tlpf_locale') {
        this.applySavedLang();
      }
    };
    window.addEventListener('storage', this.onStorage);
  }

  beforeDestroy(): void {
    if (this.onStorage) {
      window.removeEventListener('storage', this.onStorage);
    }
    this.restoreTlpfWatch();
  }
}
